import React from "react";
import PropTypes from "prop-types";
import { Card } from "primereact/card";
import Folders from "~/Pages/EntityFileDocuments/components/Folders";
import FolderSkeleton from "~/Pages/EntityFileDocuments/components/FolderSkeleton";
import emptyFolderIcon from "~/assets/empty-file.svg";

/**
 * FolderListSection — A reusable, generic folder listing component.
 * Encapsulates the common pattern used across document pages:
 *  1. Loading state  -> shows FolderSkeleton
 *  2. Filter applied -> shows heading + filterContent
 *  3. Folders exist  -> shows heading + Folders grid
 *  4. No folders     -> shows an empty state
 *
 */

function FolderListSection({
  folders,
  loading = false,
  isFilterApplied = false,
  onFolderClick,
  filterContent = null,
  heading = "Folders",
  filterHeading = "Matching Files",
  showEmptyState = false,
  emptyMessage = "No files here yet",
  emptyContent = null,
  containerClassName = "service-folder-container",
  hideSkeletonOnFilter = true,
  children = null,
}) {
  const hasFolders = folders && folders.length > 0;

  // 1. Loading state
  if (loading) {
    if (hideSkeletonOnFilter && isFilterApplied) {
      return null;
    }
    return <FolderSkeleton />;
  }

  // 2. Filter applied — show filter heading + filter content
  if (isFilterApplied) {
    return (
      <>
        <p className="document-sub-heading">{filterHeading}</p>
        {filterContent}
      </>
    );
  }

  // 3. Folders exist — show heading + folder grid
  if (hasFolders) {
    return (
      <div className={containerClassName}>
        <p className="document-sub-heading">{heading}</p>
        <Folders folders={folders} onFolderClick={onFolderClick} />
        {children}
      </div>
    );
  }

  // 4. Empty state (only when files finished loading and folders is resolved)
  if (
    showEmptyState &&
    folders != null &&
    folders !== undefined &&
    !hasFolders
  ) {
    if (emptyContent) {
      return emptyContent;
    }
    return (
      <Card>
        <div className="flex flex-column justify-content-center align-items-center p-5">
          <div className="no-documents-uploaded-section w-full gap-4">
            <img src={emptyFolderIcon} alt="empty-folder" />
            <p className="add-new-entity-heading mb-0">{emptyMessage}</p>
          </div>
        </div>
      </Card>
    );
  }

  return null;
}

FolderListSection.propTypes = {
  folders: PropTypes.array,
  loading: PropTypes.bool,
  isFilterApplied: PropTypes.bool,
  onFolderClick: PropTypes.func,
  filterContent: PropTypes.node,
  heading: PropTypes.string,
  filterHeading: PropTypes.string,
  showEmptyState: PropTypes.bool,
  emptyMessage: PropTypes.string,
  emptyContent: PropTypes.node,
  containerClassName: PropTypes.string,
  hideSkeletonOnFilter: PropTypes.bool,
  children: PropTypes.node,
};

export default FolderListSection;
